import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Sparkles, Calendar, Bell, PlayCircle, Save, Mail } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { id: 'mon', label: 'Mon' }, { id: 'tue', label: 'Tue' }, { id: 'wed', label: 'Wed' },
  { id: 'thu', label: 'Thu' }, { id: 'fri', label: 'Fri' }, { id: 'sat', label: 'Sat' }, { id: 'sun', label: 'Sun' },
];
const ROLE_OPTIONS = ['Admin', 'Purchasing', 'InventoryManager', 'ProductionSupervisor'];

const ScorecardSchedulePage = () => {
  const { api, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const isAdmin = user?.role === 'Admin';

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api().get('/admin/scorecard-nudge-settings');
      setSettings(r.data);
    } catch (e) { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: settings.enabled,
        cron_day_of_week: settings.cron_day_of_week,
        cron_hour: parseInt(settings.cron_hour),
        cron_minute: parseInt(settings.cron_minute),
        threshold: parseInt(settings.threshold),
        notify_roles: settings.notify_roles,
        send_email_summary: settings.send_email_summary,
      };
      const r = await api().put('/admin/scorecard-nudge-settings', payload);
      setSettings(r.data);
      toast.success('Schedule saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const runNow = async (apply) => {
    if (apply && !window.confirm('Run now and APPLY rating changes? This will modify vendor records and notify Purchasing users.')) return;
    setRunning(true);
    try {
      const r = await api().post('/admin/scorecard-nudge-settings/run-now', { apply });
      toast.success(`Ran (${apply ? 'applied' : 'preview'}) — ${r.data.totals.would_change} flagged${apply ? `, ${r.data.totals.applied} applied` : ''}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
    finally { setRunning(false); }
  };

  const sendTestEmail = async () => {
    const to = window.prompt('Send test email to (leave blank to use your account email):', user?.email || '');
    setRunning(true);
    try {
      const payload = to ? { to } : {};
      const r = await api().post('/admin/scorecard-nudge-settings/test-email', payload);
      if (r.data?.status === 'sent') toast.success(`Test sent (id: ${(r.data.id || '').slice(0, 8) || 'ok'})`);
      else if (r.data?.status === 'skipped') toast.warning(`Skipped: ${r.data.reason}`);
      else toast.error(`Failed: ${r.data?.error || 'unknown'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test email failed'); }
    finally { setRunning(false); }
  };

  const toggleRole = (role) => {
    setSettings(s => ({
      ...s,
      notify_roles: s.notify_roles.includes(role) ? s.notify_roles.filter(r => r !== role) : [...s.notify_roles, role],
    }));
  };

  if (loading || !settings) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-slideIn max-w-3xl" data-testid="scorecard-schedule-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-6 h-6 text-amber-500" /> Vendor Scorecard Auto-Nudge</h1>
        <p className="text-slate-500 text-sm">Closes the quality-feedback loop. Automatically reduces star ratings for vendors with high disposal/recall activity, posts an in-app notification + (when configured) an email summary to Purchasing.</p>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /> Schedule</span>
            <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} disabled={!isAdmin} data-testid="schedule-enabled-switch" />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div>
            <Label>Day of Week</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {DAYS.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => isAdmin && setSettings({ ...settings, cron_day_of_week: d.id })}
                  className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${settings.cron_day_of_week === d.id ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} ${isAdmin ? '' : 'opacity-60 cursor-not-allowed'}`}
                  data-testid={`schedule-day-${d.id}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Hour (UTC)</Label><Input type="number" min={0} max={23} value={settings.cron_hour} onChange={(e) => setSettings({ ...settings, cron_hour: e.target.value })} className="rounded-sm mono" disabled={!isAdmin} data-testid="schedule-hour" /></div>
            <div><Label>Minute</Label><Input type="number" min={0} max={59} value={settings.cron_minute} onChange={(e) => setSettings({ ...settings, cron_minute: e.target.value })} className="rounded-sm mono" disabled={!isAdmin} data-testid="schedule-minute" /></div>
            <div><Label>Threshold (events)</Label><Input type="number" min={1} value={settings.threshold} onChange={(e) => setSettings({ ...settings, threshold: e.target.value })} className="rounded-sm mono" disabled={!isAdmin} data-testid="schedule-threshold" /></div>
          </div>
          <div className="text-xs text-slate-500 italic">Next run: every <strong className="text-slate-700">{DAYS.find(d => d.id === settings.cron_day_of_week)?.label}</strong> at <strong className="text-slate-700 mono">{String(settings.cron_hour).padStart(2, '0')}:{String(settings.cron_minute).padStart(2, '0')} UTC</strong>. Vendors with <strong>≥ {settings.threshold}</strong> disposal+recall events in 90d auto-decrement.</div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4 text-slate-400" /> Notifications</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <div>
            <Label>Notify roles</Label>
            <div className="flex gap-2 mt-2 flex-wrap" data-testid="schedule-notify-roles">
              {ROLE_OPTIONS.map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => isAdmin && toggleRole(r)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-medium ${settings.notify_roles.includes(r) ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} ${isAdmin ? '' : 'opacity-60 cursor-not-allowed'}`}
                  data-testid={`schedule-role-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center justify-between gap-2 text-sm" data-testid="schedule-email-toggle">
            <div className="flex items-center gap-2">
              <Switch checked={settings.send_email_summary} onCheckedChange={(v) => isAdmin && setSettings({ ...settings, send_email_summary: v })} disabled={!isAdmin} />
              <span>Also send email summary <span className="text-xs text-emerald-600">(Resend integrated)</span></span>
            </div>
            <Button size="sm" variant="outline" onClick={sendTestEmail} disabled={running} data-testid="schedule-test-email"><Mail className="w-4 h-4 mr-1" /> Test Email</Button>
          </label>
        </CardContent>
      </Card>

      {settings.last_run_at && (
        <Card className="industrial-card border-slate-200">
          <CardContent className="p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-slate-500">Last Run</span>
              <span className="text-xs text-slate-500">{new Date(settings.last_run_at).toLocaleString()}</span>
            </div>
            {settings.last_run_summary && (
              <div className="mt-2 grid grid-cols-3 gap-3">
                <div><span className="text-xs text-slate-500 block">Evaluated</span><span className="text-lg font-bold mono">{settings.last_run_summary.vendors_evaluated || 0}</span></div>
                <div><span className="text-xs text-slate-500 block">Flagged</span><span className="text-lg font-bold mono text-amber-700">{settings.last_run_summary.would_change || 0}</span></div>
                <div><span className="text-xs text-slate-500 block">Applied</span><span className="text-lg font-bold mono text-emerald-700">{settings.last_run_summary.applied || 0}</span></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={() => runNow(false)} disabled={running} data-testid="schedule-run-preview"><PlayCircle className="w-4 h-4 mr-1" /> Preview Run</Button>
        <Button onClick={() => runNow(true)} disabled={running || !isAdmin} className="bg-amber-600 hover:bg-amber-700" data-testid="schedule-run-now"><Sparkles className="w-4 h-4 mr-1" /> Run Now (Apply)</Button>
        <Button onClick={save} disabled={saving || !isAdmin} className="bg-slate-900 hover:bg-slate-800" data-testid="schedule-save"><Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Schedule'}</Button>
      </div>
    </div>
  );
};

export default ScorecardSchedulePage;
